import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  Link as ChakraLink,
  Icon,
  Spacer,
  Button,
  Text,
  Avatar,
  Divider,
  Select,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiMessageSquare, FiLogOut, FiMenu, FiHeart, FiServer, FiBook, FiSliders, FiUsers, FiTrendingUp, FiTool, FiKey } from 'react-icons/fi';

import LoginForm from './components/Login';
import Chat from './components/Chat';
import HealthCheck from './components/HealthCheck';
import PostgresStatus from './components/PostgresStatus';
import ApiDocs from './components/ApiDocs';
import LLMProvider from './components/LLMProvider';
import Users from './components/Users';
import Usage from './components/Usage';
import Tools from './components/Tools';
import McpKeys from './components/McpKeys';

const API_BASE = '';

const NavButton = ({ to, icon, children, isActive }) => (
  <ChakraLink as={RouterLink} to={to} _hover={{ textDecoration: 'none' }}>
    <Button
      leftIcon={<Icon as={icon} boxSize={5} />}
      width="full"
      justifyContent="flex-start"
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'teal' : 'whiteAlpha'}
      color={isActive ? 'white' : 'whiteAlpha.800'}
      bg={isActive ? 'whiteAlpha.200' : 'transparent'}
      _hover={{
        bg: isActive ? 'whiteAlpha.300' : 'whiteAlpha.100',
        color: 'white',
      }}
      borderRadius="lg"
      py={6}
      fontSize="md"
      fontWeight={isActive ? '600' : '400'}
    >
      {children}
    </Button>
  </ChakraLink>
);

function App() {
  const [token, setToken] = useState(localStorage.getItem('assistant_token'));
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [tenantsError, setTenantsError] = useState(null);
  const [tenantLocked, setTenantLocked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => { onClose(); }, [location.pathname, onClose]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/v1/tenants`, { headers: { Authorization: `Token ${token}` } })
      .then((resp) => resp.json())
      .then((data) => setTenants(data.tenants || []))
      .catch(() => setTenantsError('Could not load instances — try refreshing.'));
  }, [token]);

  const handleLogin = () => {
    setToken(localStorage.getItem('assistant_token'));
    navigate('/');
  };

  const handleLogout = () => {
    localStorage.removeItem('assistant_token');
    setToken(null);
  };

  if (!token) {
    return <LoginForm onLogin={handleLogin} apiBase={API_BASE} />;
  }

  const renderSidebarContent = () => (
    <>
      <Flex align="center" px={4} mb={6}>
        <Box w={12} h={12} borderRadius="full" overflow="hidden" bg="white" p={0.5}>
          <img src="/static/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        </Box>
        <Box ml={3}>
          <Text fontSize="lg" fontWeight="bold" color="white">Assistant</Text>
        </Box>
      </Flex>

      <Divider borderColor="whiteAlpha.300" mb={4} />

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mb={2}>
        Ask
      </Text>
      <NavButton to="/" icon={FiMessageSquare} isActive={location.pathname === '/'}>
        Chat
      </NavButton>
      <NavButton to="/tools" icon={FiTool} isActive={location.pathname === '/tools'}>
        Tools
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Instance
      </Text>
      <Box px={4}>
        <Select
          placeholder="Select an instance..."
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          isDisabled={tenantLocked}
          size="sm"
          bg="whiteAlpha.200"
          color="white"
          borderColor="whiteAlpha.300"
          sx={{ '> option': { color: 'black' } }}
        >
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </Select>
        {tenantsError && <Text color="red.300" fontSize="xs" mt={1}>{tenantsError}</Text>}
        {tenantLocked && (
          <Text color="whiteAlpha.600" fontSize="xs" mt={1}>Locked for this conversation — start a new chat to switch.</Text>
        )}
      </Box>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Insights
      </Text>
      <NavButton to="/health" icon={FiHeart} isActive={location.pathname === '/health'}>
        Health Check
      </NavButton>
      <NavButton to="/usage" icon={FiTrendingUp} isActive={location.pathname === '/usage'}>
        Usage &amp; Discussions
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Infrastructure
      </Text>
      <NavButton to="/postgres" icon={FiServer} isActive={location.pathname === '/postgres'}>
        PostgreSQL Status
      </NavButton>
      <NavButton to="/llm-provider" icon={FiSliders} isActive={location.pathname === '/llm-provider'}>
        AI Provider
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Administration
      </Text>
      <NavButton to="/users" icon={FiUsers} isActive={location.pathname === '/users'}>
        Users
      </NavButton>
      <NavButton to="/mcp-keys" icon={FiKey} isActive={location.pathname === '/mcp-keys'}>
        MCP API Keys
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Developer
      </Text>
      <NavButton to="/api" icon={FiBook} isActive={location.pathname === '/api'}>
        API Documentation
      </NavButton>

      <Spacer />

      <Divider borderColor="whiteAlpha.300" />
      <Flex align="center" px={4} py={3}>
        <Avatar size="sm" bg="teal.400" />
        <Box ml={3} flex={1}>
          <Text fontSize="sm" color="white" fontWeight="medium">Admin User</Text>
          <Text fontSize="xs" color="whiteAlpha.600">Logged in</Text>
        </Box>
      </Flex>
      <Button
        leftIcon={<Icon as={FiLogOut} />}
        onClick={handleLogout}
        width="full"
        justifyContent="flex-start"
        variant="ghost"
        color="whiteAlpha.800"
        _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
        borderRadius="lg"
      >
        Logout
      </Button>
    </>
  );

  return (
    <Flex direction="column" height="100vh" bg="gray.50">
      <Flex
        display={{ base: 'flex', md: 'none' }}
        flexShrink={0}
        height="56px"
        bg="purple.800"
        align="center"
        px={4}
        justify="space-between"
        boxShadow="md"
      >
        <IconButton
          icon={<Icon as={FiMenu} />}
          onClick={onOpen}
          variant="ghost"
          color="white"
          aria-label="Open navigation"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
        <Text color="white" fontWeight="bold" fontSize="md">Assistant</Text>
        <IconButton
          icon={<Icon as={FiLogOut} />}
          onClick={handleLogout}
          variant="ghost"
          color="white"
          aria-label="Logout"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
      </Flex>

      <Flex flex="1" overflow="hidden">
        <VStack
          as="nav"
          display={{ base: 'none', md: 'flex' }}
          width="280px"
          py={6}
          px={4}
          bgGradient="linear(to-b, purple.700, purple.900)"
          spacing={2}
          align="stretch"
          boxShadow="xl"
          overflowY="auto"
          flexShrink={0}
        >
          {renderSidebarContent()}
        </VStack>

        <Box
          flex="1"
          overflow={location.pathname === '/' ? 'hidden' : 'auto'}
          p={location.pathname === '/' ? 0 : { base: 4, md: 8 }}
        >
          <Routes>
            <Route
              path="/"
              element={(
                <Chat
                  token={token}
                  apiBase={API_BASE}
                  tenantId={tenantId}
                  onSessionStarted={() => setTenantLocked(true)}
                  onNewChat={() => setTenantLocked(false)}
                />
              )}
            />
            <Route path="/tools" element={<Tools />} />
            <Route path="/health" element={<HealthCheck />} />
            <Route path="/usage" element={<Usage />} />
            <Route path="/postgres" element={<PostgresStatus />} />
            <Route path="/llm-provider" element={<LLMProvider />} />
            <Route path="/users" element={<Users />} />
            <Route path="/mcp-keys" element={<McpKeys />} />
            <Route path="/api" element={<ApiDocs />} />
          </Routes>
        </Box>
      </Flex>

      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent bgGradient="linear(to-b, purple.700, purple.900)" maxW="280px">
          <DrawerCloseButton color="white" top={4} />
          <DrawerBody p={0}>
            <VStack spacing={2} align="stretch" py={6} px={4} minH="100%">
              {renderSidebarContent()}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

export default App;
